import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  ApiError,
  organizationsApi,
  templatesApi,
  type OrganizationSettingsResponseDTO,
  type VerticalTemplateDTO,
} from '@/shared/api'

interface Props {
  /** Role of the currently authenticated user. */
  currentRole: string | null
}

function errorDetail(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.detail || fallback
  if (err instanceof Error) return err.message
  return fallback
}

/**
 * Workspace settings card. Owner/admin can change the vertical template,
 * default language, and timezone. Everyone else gets a read-only snapshot.
 */
export function WorkspaceSection({ currentRole }: Props) {
  const canEdit = currentRole === 'owner' || currentRole === 'admin'

  const [data, setData] = useState<OrganizationSettingsResponseDTO | null>(null)
  const [templates, setTemplates] = useState<VerticalTemplateDTO[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [template, setTemplate] = useState('generic')
  const [language, setLanguage] = useState('pt-BR')
  const [timezone, setTimezone] = useState('UTC')
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)
  const [saved, setSaved] = useState(false)

  const refresh = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [settings, tpls] = await Promise.all([
        organizationsApi.getCurrentSettings(),
        templatesApi.list(),
      ])
      setData(settings)
      setTemplates(tpls.items)
      setTemplate(settings.settings.vertical_template)
      setLanguage(settings.settings.default_language)
      setTimezone(settings.settings.default_timezone)
    } catch (err) {
      setError(errorDetail(err, 'Falha ao carregar configurações.'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh])

  const handleSave = useCallback(
    async (event: React.FormEvent) => {
      event.preventDefault()
      if (saving) return
      setSaving(true)
      setSaveError(null)
      setSaved(false)
      try {
        const updated = await organizationsApi.updateCurrentSettings({
          vertical_template: template,
          default_language: language,
          default_timezone: timezone,
        })
        setData(updated)
        setSaved(true)
        setTimeout(() => setSaved(false), 2500)
      } catch (err) {
        setSaveError(errorDetail(err, 'Falha ao salvar.'))
      } finally {
        setSaving(false)
      }
    },
    [language, saving, template, timezone],
  )

  const handleCancel = useCallback(() => {
    if (!data) return
    setTemplate(data.settings.vertical_template)
    setLanguage(data.settings.default_language)
    setTimezone(data.settings.default_timezone)
    setSaveError(null)
  }, [data])

  const isDirty = useMemo(() => {
    if (!data) return false
    return (
      template !== data.settings.vertical_template ||
      language !== data.settings.default_language ||
      timezone !== data.settings.default_timezone
    )
  }, [data, language, template, timezone])

  if (loading && !data) {
    return (
      <div className="settings-section">
        <p className="settings-empty">Carregando configurações…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="settings-section">
        <div className="settings-alert is-error" role="alert">
          {error}
        </div>
        <button type="button" className="settings-button" onClick={() => void refresh()}>
          Tentar novamente
        </button>
      </div>
    )
  }

  if (!data) {
    return null
  }

  return (
    <div className="settings-section">
      <header className="settings-section-head">
        <h3>Workspace</h3>
        <p className="settings-section-help">
          Identidade do workspace, vertical e defaults regionais. O template
          define labels e exemplos usados em toda a UI.
        </p>
      </header>

      <div className="settings-card">
        <div>
          <strong>{data.organization.display_name}</strong>
          <div className="settings-meta">
            tenant: <code>{data.organization.tenant_id}</code> · slug:{' '}
            <code>{data.organization.slug}</code>
          </div>
        </div>
      </div>

      <form className="settings-form" onSubmit={(e) => void handleSave(e)}>
        <label className="settings-field">
          <span className="settings-field-label">Template vertical</span>
          <select
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            disabled={!canEdit || saving}
            className="settings-input"
          >
            {templates.map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
              </option>
            ))}
          </select>
          <span className="settings-field-help">
            {templates.find((t) => t.id === template)?.description}
          </span>
        </label>

        <label className="settings-field">
          <span className="settings-field-label">Idioma padrão</span>
          <input
            type="text"
            value={language}
            onChange={(e) => setLanguage(e.target.value)}
            placeholder="pt-BR"
            disabled={!canEdit || saving}
            className="settings-input"
          />
          <span className="settings-field-help">
            Tag BCP-47 simples (ex.: pt-BR, en-US, es-ES, fr).
          </span>
        </label>

        <label className="settings-field">
          <span className="settings-field-label">Fuso horário</span>
          <input
            type="text"
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            placeholder="America/Sao_Paulo"
            disabled={!canEdit || saving}
            className="settings-input"
          />
          <span className="settings-field-help">
            Nome IANA (ex.: UTC, America/Sao_Paulo, Europe/Lisbon).
          </span>
        </label>

        {saveError ? (
          <div className="settings-alert is-error" role="alert">
            {saveError}
          </div>
        ) : null}
        {saved ? (
          <div className="settings-alert is-success" role="status">
            Configurações salvas.
          </div>
        ) : null}

        {canEdit ? (
          <div className="settings-form-actions">
            <button
              type="submit"
              className="settings-button is-primary"
              disabled={!isDirty || saving}
            >
              {saving ? 'Salvando…' : 'Salvar alterações'}
            </button>
            <button
              type="button"
              className="settings-button"
              onClick={handleCancel}
              disabled={!isDirty || saving}
            >
              Cancelar
            </button>
          </div>
        ) : (
          <p className="settings-field-help">
            Apenas owners e admins podem alterar essas configurações.
          </p>
        )}
      </form>
    </div>
  )
}
